/**
 * <s-tap-peek> — touch counterpart of a hover preview.
 *
 * On devices without hover, a tap toggles data-open on the element so CSS can show its preview
 * (colour pop-up, model photo). A tap elsewhere, scrolling or Escape closes it. On mouse devices
 * it does nothing; the hover styles handle the preview there. The tap still reaches the
 * surrounding label, so selecting the colour or set partner keeps working.
 */
class STapPeek extends HTMLElement {
  #ac;
  #outside;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.addEventListener('click', (event) => {
      // A label forwards a second, synthetic click to its input; count only the real tap.
      if (event.target instanceof HTMLInputElement) return;
      if (!window.matchMedia('(hover: none)').matches) return;
      this.#toggle(!this.hasAttribute('data-open'));
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#outside?.abort();
  }

  #toggle(open) {
    // Only one preview open at a time.
    if (open) document.querySelectorAll('s-tap-peek[data-open]').forEach((el) => el !== this && el.removeAttribute('data-open'));
    this.toggleAttribute('data-open', open);
    this.#outside?.abort();
    if (!open) return;
    this.#outside = new AbortController();
    const opts = { signal: this.#outside.signal, passive: true };
    const close = () => this.#toggle(false);
    // Defer so the opening tap does not close it again.
    requestAnimationFrame(() => {
      document.addEventListener('pointerdown', (event) => {
        if (!this.contains(event.target)) close();
      }, opts);
      window.addEventListener('scroll', close, { ...opts, once: true });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') close();
      }, { signal: this.#outside.signal });
    });
  }
}

if (!customElements.get('s-tap-peek')) {
  customElements.define('s-tap-peek', STapPeek);
}
